// import { BlobServiceClient, StorageSharedKeyCredential } from '@azure/storage-blob'
import { Box, FormControl, Grid, Link, MenuItem, Select, SelectChangeEvent, Tooltip, Typography } from '@mui/material'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { resourceReports } from '../../api/services/resourceReports.service'
import { useCurrentSite, useWorkingAddress } from '../../store/store.hooks'
import theme from '../../theme'
import { ResourceReport } from '../../types'

const ReportsComponent = ({ isConnected, productName }: { isConnected: boolean; productName: string }) => {
  const { t } = useTranslation()
  const serverIP = useWorkingAddress()
  const currentSite = useCurrentSite()

  const [reportsData, setReportsData] = useState<ResourceReport[]>([])
  const [selectedResourceId, setSelectedResourceId] = useState<string>('')

  useEffect(() => {
    const loadResourceReport = async () => {
      if (!currentSite) return
      try {
        const data = await resourceReports.list(currentSite.id, productName)
        setReportsData(data as ResourceReport[])
        if (data.length > 0) {
          setSelectedResourceId(data[0].resourceId)
        }
      } catch (error) {
        console.error('Error fetching report data:', error)
      }
    }

    loadResourceReport()
  }, [currentSite, productName])

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelectedResourceId(event.target.value as string)
  }
  const selectedReport = useMemo(
    () => reportsData.find((report) => report.resourceId === selectedResourceId),
    [reportsData, selectedResourceId],
  )

  const [disabledLinks, setDisabledLinks] = useState<{ [key: string]: boolean }>({})
  const blobUrlsRef = useRef<string[]>([])

  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach((url) => window.URL.revokeObjectURL(url))
    }
  }, [])

  const openReport = async (href: string) => {
    if (!href || !currentSite) {
      return
    }

    // Open blank window synchronously (user-initiated → not blocked by browser)
    const newWindow = window.open('', '_blank')

    try {
      const pdfBlob = await resourceReports.downloadPdf(currentSite.id, href)
      const blobUrl = window.URL.createObjectURL(pdfBlob)
      blobUrlsRef.current.push(blobUrl)

      if (newWindow) {
        newWindow.location.href = blobUrl
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl)
          blobUrlsRef.current = blobUrlsRef.current.filter((u) => u !== blobUrl)
        }, 10_000)
      } else {
        // Popup was blocked — clean up immediately
        window.URL.revokeObjectURL(blobUrl)
        blobUrlsRef.current = blobUrlsRef.current.filter((u) => u !== blobUrl)
      }
    } catch (error) {
      console.error('Error opening report PDF:', error)
      newWindow?.close()
      setDisabledLinks((prev) => ({ ...prev, [href]: true }))
    }
  }

  const renderLink = (href: string, label: string) => {
    const disabled = !href || disabledLinks[href] || false

    return (
      <Tooltip title={disabled ? t(`resourceReports.unavailable`) : ''} placement="right">
        <Link
          component="button"
          aria-disabled={disabled}
          onClick={(event) => {
            event.preventDefault()
            if (disabled) {
              return
            }

            void openReport(href)
          }}
          sx={{
            textDecoration: !disabled ? 'underline' : 'none',
            color: !disabled ? 'inherit' : 'grey',
            cursor: !disabled ? 'pointer' : 'not-allowed',
            background: 'none',
            border: 'none',
            p: 0,
          }}
        >
          {label}
        </Link>
      </Tooltip>
    )
  }

  const reportSections = useMemo(
    () => [
      {
        title: t('resourceReports.shift'),
        links: [
          { href: selectedReport?.currentShift || '', label: t('resourceReports.current') },
          { href: selectedReport?.lastShift || '', label: t('resourceReports.last') },
        ],
      },
      {
        title: t('resourceReports.day'),
        links: [
          { href: selectedReport?.today || '', label: t('resourceReports.today') },
          { href: selectedReport?.yesterday || '', label: t('resourceReports.yesterday') },
        ],
      },
      {
        title: t('resourceReports.week'),
        links: [
          { href: selectedReport?.currentWeek || '', label: t('resourceReports.current') },
          { href: selectedReport?.lastWeek || '', label: t('resourceReports.last') },
        ],
      },
      {
        title: t('resourceReports.month'),
        links: [
          { href: selectedReport?.currentMonth || '', label: t('resourceReports.current') },
          { href: selectedReport?.lastMonth || '', label: t('resourceReports.last') },
        ],
        fiscalLinks: [
          { href: selectedReport?.currentFiscalMonth || '', label: t('resourceReports.currentFiscal') },
          { href: selectedReport?.lastFiscalMonth || '', label: t('resourceReports.lastFiscal') },
        ],
      },
      {
        title: t('resourceReports.year'),
        links: [
          { href: selectedReport?.currentYear || '', label: t('resourceReports.current') },
          { href: selectedReport?.lastYear || '', label: t('resourceReports.last') },
        ],
        fiscalLinks: [
          { href: selectedReport?.currentFiscalYear || '', label: t('resourceReports.currentFiscal') },
          { href: selectedReport?.lastFiscalYear || '', label: t('resourceReports.lastFiscal') },
        ],
      },
    ],
    [selectedReport, t],
  )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', padding: 1, backgroundColor: '#0072CE' }}>
        <Typography variant="body2" sx={{ fontSize: '17px', fontWeight : 600, color: '#ffffff', textTransform: 'uppercase' }}>
          {t('resourceReports.reports')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 2 }}>
        <FormControl sx={{ width: '100%', background: theme.palette.background.default }}>
          <Select
            value={selectedResourceId}
            onChange={handleChange}
            displayEmpty
            renderValue={(selected) => {
              if (selected === '') {
                return <span style={{ color: 'grey' }}>{t('resourceReports.selectEquipment')}</span>
              }
              return reportsData.find((report) => report.resourceId === selected)?.displayName
            }}
          >
            <MenuItem value="">
              <span style={{ color: 'grey' }}>{t('resourceReports.selectEquipment')}</span>
            </MenuItem>
            {reportsData.map((report) => (
              <MenuItem key={report.resourceId} value={report.resourceId}>
                {report.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ m: 2 }}>
        <Grid container spacing={3} alignItems="center">
          {reportSections.map(({ title, links, fiscalLinks }, index) => (
            <Fragment key={index}>
              <Grid size={3.9}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {title}
                </Typography>
              </Grid>
              <Grid size={8.1}>
                <Box>
                  {links.map(({ href, label }, linkIndex) => (
                    <Fragment key={linkIndex}>
                      {linkIndex > 0 && ' | '}
                      {renderLink(href, label)}
                    </Fragment>
                  ))}
                </Box>
                {fiscalLinks && (
                  <Box sx={{ mt: 1 }}>
                    {fiscalLinks.map(({ href, label }, linkIndex) => (
                      <Fragment key={linkIndex}>
                        {linkIndex > 0 && ' | '}
                        {renderLink(href, label)}
                      </Fragment>
                    ))}
                  </Box>
                )}
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </Box>
      <Box sx={{ flex: 1 }} />
      <Box
      id="reports-section" 
      sx={{ m: 2 }}>
        <Tooltip title={t('connection.dashboardAndToolsNotAvailable')} followCursor>
          <Typography variant="body1" component="span">
            <a
              {...(isConnected
                ? { href: `http://${serverIP}:49990/Reports`, target: '_blank', rel: 'noopener noreferrer' }
                : {})}
              style={{
                textDecoration: isConnected ? 'underline' : 'none',
                color: isConnected ? 'inherit' : 'grey',
                cursor: isConnected ? 'pointer' : 'not-allowed',
              }}
            >
              {t('resourceReports.browseAllReports').split(' ')[0]}
            </a>{' '}
            {t('resourceReports.browseAllReports').replace(t('resourceReports.browseAllReports').split(' ')[0], '')}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default ReportsComponent
